import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  type CardProps,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
} from '@chakra-ui/react';
import { stringify } from 'flatted';
import mixpanel from 'mixpanel-browser';
import { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  ActionModal,
  FeatureTrackingEvents,
  getPlotColorLookup,
} from '@revelio/core';
import { Button } from '@revelio/ui';

import { DashMetaRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { createSharedPlot } from '../../api/shared-plots-api';
import { useToast } from '../../hooks/useToast';
import styles from '../Modal/themedModal.module.css';
import { useSharedReportData } from '../SharedReport/SharedReportDataContext';
import { useSafeChart } from './ChartProvider';
import localStyles from './PlotLinkModal.module.css';

type PlotLinkModalStateType = {
  isPlotLinkOpen: boolean;
  onPlotLinkOpen: () => void;
  onPlotLinkClose: () => void;
};

export interface PlotLinkModalProps {
  plotLinkModalState: PlotLinkModalStateType;
  cardConfig?: Partial<CardProps> & { header?: string };
  typeAndProps?: unknown;
  data: unknown;
  filters?: Record<string, unknown>;
  /**
   * When true, generate the link via the dash-meta shared-plots backend
   * (`/shared/plot/<token>`). Otherwise fall through to the legacy Firebase
   * plotShare cloud function.
   */
  useV2SharePath?: boolean;
}

export function PlotLinkModal({
  plotLinkModalState,
  cardConfig,
  typeAndProps,
  data,
  filters = {},
  useV2SharePath = false,
}: PlotLinkModalProps) {
  const { isPlotLinkOpen: isOpen, onPlotLinkClose: onClose } =
    plotLinkModalState;

  const { loggedInUser } = useGetLoggedInUser();
  const sharedReportData = useSharedReportData();
  const { item } = useSafeChart();
  const toast = useToast();

  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  const dataObject: any = {
    plotData: data,
    cardConfig: cardConfig,
    typeAndProps: typeAndProps,
    userID: loggedInUser.id,
    colors: getPlotColorLookup(),
    filters,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [checkData, setCheckData] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    setCheckData('');
  }, [
    plotLinkModalState.isPlotLinkOpen,
    dataObject.cardConfig.endpointSegment,
    dataObject.cardConfig.header,
    dataObject.cardConfig.view,
    dataObject.cardConfig.viewType,
    dataObject.plotData?.length,
    dataObject.plotData?.links?.length,
    dataObject.plotData?.nodes?.length,
  ]);

  const showShareErrorToast = (rateLimited: boolean) => {
    toast({
      position: 'top-right',
      title: rateLimited
        ? 'Too many share links generated'
        : 'Failed to generate share link',
      description: rateLimited
        ? 'Please try again later.'
        : 'Please try again.',
      status: rateLimited ? 'warning' : 'error',
      duration: rateLimited ? 5000 : 3000,
      isClosable: true,
    });
  };

  const createPlotLinkV2 = async () => {
    const captured = item
      ? sharedReportData?.getAllChartData()[item.id]
      : undefined;
    if (!captured?.component) {
      console.error('No captured chart data for plot share');
      showShareErrorToast(false);
      setIsLoading(false);
      return;
    }

    try {
      const { token } = await createSharedPlot({
        title: cardConfig?.header ?? '',
        component: captured.component,
        props: captured.props ?? {},
      });
      setCheckData(`${window.location.origin}/shared/plot/${token}`);
    } catch (error) {
      console.error('Error generating plot share link:', error);
      showShareErrorToast(error instanceof DashMetaRateLimitError);
    } finally {
      setIsLoading(false);
    }
  };

  const createPlotLinkLegacy = async () => {
    delete dataObject.cardConfig.footer;
    const finalObject = {
      meta: {
        userID: dataObject.userID,
        ...dataObject.cardConfig,
      },
      config: {
        ...dataObject.typeAndProps.chartProps,
        colorLookup: dataObject.colors,
      },
      type: dataObject.typeAndProps.chartType,
      data: Array.isArray(dataObject.plotData)
        ? dataObject.plotData
        : stringify(dataObject.plotData),
      filters: dataObject.filters,
    };
    try {
      const response = await fetch(
        'https://us-central1-dashboard-ui-revelio.cloudfunctions.net/plotShare',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(finalObject),
        }
      );
      const jsonData = await response.json();
      setCheckData(
        jsonData.id
          ? `https://charts.reveliolabs.com/dashboard/${jsonData.id}`
          : ''
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createPlotLink = async () => {
    mixpanel.track(FeatureTrackingEvents.PLOT_LINK_GENERATE, {
      plot_title: cardConfig?.header,
      version: useV2SharePath ? 'v2' : 'legacy',
    });

    setIsLoading(true);

    if (useV2SharePath) {
      await createPlotLinkV2();
      return;
    }

    await createPlotLinkLegacy();
  };

  const [copiedGeneratedPlotLink, setCopiedGeneratedPlotLink] = useState(false);
  const copyTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutIdRef.current) {
        clearTimeout(copyTimeoutIdRef.current);
      }
    };
  }, []);

  const handleCopyGeneratedPlotLink = () => {
    if (copyTimeoutIdRef.current) {
      clearTimeout(copyTimeoutIdRef.current);
    }

    setCopiedGeneratedPlotLink(true);

    copyTimeoutIdRef.current = setTimeout(() => {
      setCopiedGeneratedPlotLink(false);
      copyTimeoutIdRef.current = null;
    }, 2000);
  };
  return (
    <ActionModal
      header="Shareable plot link"
      isOpen={isOpen}
      onClose={onClose}
      modalContentProps={{ className: styles.content }}
    >
      <ModalBody>
        <Flex direction="column" rowGap="1rem">
          <Stack spacing={1}>
            <Text fontSize="sm">Anyone with this link can view this plot</Text>
            <InputGroup size="md">
              <Input
                pr={checkData !== '' ? '6rem' : '7.5rem'}
                value={checkData === '' ? '' : checkData}
                placeholder="No shared link"
                disabled={checkData === ''}
              />
              <InputRightElement width={checkData !== '' ? '6rem' : '7.5rem'}>
                {checkData !== '' ? (
                  <CopyToClipboard
                    text={checkData}
                    onCopy={handleCopyGeneratedPlotLink}
                  >
                    <Button
                      className={localStyles.copyBtn}
                      size="sm"
                      color="primary"
                      variant="default"
                    >
                      {copiedGeneratedPlotLink ? (
                        <>
                          <CheckIcon mr={1} /> Copied
                        </>
                      ) : (
                        'Copy Link'
                      )}
                    </Button>
                  </CopyToClipboard>
                ) : (
                  <Button
                    className={localStyles.copyBtn}
                    size="sm"
                    color="primary"
                    variant="default"
                    loading={isLoading}
                    onClick={createPlotLink}
                  >
                    Generate Link
                  </Button>
                )}
              </InputRightElement>
            </InputGroup>
            {checkData !== '' && (
              <Box pt={1}>
                <Link
                  fontSize="sm"
                  textDecoration="underline"
                  _hover={{ textDecoration: 'none' }}
                  cursor="pointer"
                  href={checkData}
                  isExternal
                  target="_blank"
                  rel="noreferrer"
                  className={localStyles.previewLink}
                >
                  Preview link in new tab
                </Link>
              </Box>
            )}
          </Stack>
        </Flex>
      </ModalBody>
      <ModalFooter></ModalFooter>
    </ActionModal>
  );
}

export default PlotLinkModal;
